import { useState } from 'react'
import { toast } from 'sonner'
import { useConfiguracionEstudio } from '@/hooks/useConfiguracion'
import {
  ConfiguracionEstudioNoDisponibleError,
  exigirConfiguracionEstudio,
} from '@/services/configuracionService'
import type { DatosVencimientosClientePDF, VencimientoPDF } from '@/lib/pdfVencimientosCliente'

interface ClienteParaPDF {
  nombre: string
  cuit?: string
  condicionIva?: string
}

interface VencimientoParaPDF {
  impuesto_nombre?: string | null
  tipo_impuesto?: string | null
  periodo?: string | null
  fecha_vencimiento?: string | null
  estado?: string | null
  monto_determinado?: number | null
  observaciones?: string | null
}

export function useDescargarVencimientosPDF() {
  const { data: configData, isError: configError } = useConfiguracionEstudio()
  const [generando, setGenerando] = useState(false)

  const descargar = async (
    cliente: ClienteParaPDF,
    vencimientos: VencimientoParaPDF[],
    filename?: string,
  ) => {
    setGenerando(true)
    try {
      const config = exigirConfiguracionEstudio({ data: configData, isError: configError })

      const [{ pdf }, { VencimientosClientePDF }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('@/lib/pdfVencimientosCliente'),
      ])

      const estudio = {
        nombre: config?.nombre ?? 'Estudio Contable',
        cuit: config?.cuit ?? '',
        direccion: config?.direccion ?? '',
        telefono: config?.telefono ?? '',
        email: config?.email ?? '',
      }

      // Map vencimientos to PDF format
      const vencimientosPDF: VencimientoPDF[] = vencimientos.map((v) => ({
        impuesto: v.impuesto_nombre ?? v.tipo_impuesto ?? '',
        periodo: v.periodo ?? '',
        fecha_vencimiento: v.fecha_vencimiento ?? '',
        estado: v.estado ?? 'pendiente',
        monto_determinado: v.monto_determinado,
        observaciones: v.observaciones,
      }))

      // Calculate summary
      const resumen = {
        pendientes: vencimientosPDF.filter((v) => v.estado === 'pendiente').length,
        presentados: vencimientosPDF.filter((v) => v.estado === 'presentado').length,
        vencidos: vencimientosPDF.filter((v) => v.estado === 'vencido').length,
        pagados: vencimientosPDF.filter((v) => v.estado === 'pagado').length,
        total: vencimientosPDF.length,
      }

      const datos: DatosVencimientosClientePDF = {
        estudio,
        cliente,
        vencimientos: vencimientosPDF,
        resumen,
      }

      const blob = await pdf(
        <VencimientosClientePDF datos={datos} />
      ).toBlob()

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename ?? `vencimientos-${cliente.nombre.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error generando el PDF de vencimientos:', error)
      toast.error(
        error instanceof ConfiguracionEstudioNoDisponibleError
          ? error.message
          : 'No se pudo generar el PDF de vencimientos',
      )
    } finally {
      setGenerando(false)
    }
  }

  return { descargar, generando }
}
